import { useState } from "react";
import { C } from "../lib/theme";
import { Btn } from "../components/Btn";
import { KapioLogo } from "../components/KapioLogo";
import { supabase } from "../lib/supabase";

export function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const canSubmit = password.length >= 8 && password === confirm && !busy;

  async function onSubmit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (err) {
      const msg = (err.message || "").toLowerCase();
      if (msg.includes("leaked") || msg.includes("easy to guess")) {
        setError("This password has appeared in a known data breach. Please choose a stronger, unique password.");
      } else if (msg.includes("same") || msg.includes("different from the old")) {
        setError("New password must be different from your previous password.");
      } else {
        setError(err.message || "Failed to update password");
      }
      return;
    }
    setSuccess(true);
    // Sign out the recovery session after a brief success display. The
    // onAuthStateChange SIGNED_OUT handler in useAuth clears recovery state
    // and scrubs the token fragment from the URL.
    setTimeout(async () => {
      await supabase.auth.signOut({ scope: "local" });
    }, 1800);
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.bg }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 24 }}>
          <KapioLogo size={36} />
          <span style={{ fontSize: 22, fontWeight: 900, color: C.hunter, letterSpacing: "-0.5px" }}>Kapio</span>
        </div>

        <div
          style={{
            background: C.white,
            border: "1px solid rgba(196,193,183,0.35)",
            borderRadius: 20,
            padding: 28,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          {success ? (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: C.greenBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 14px",
                }}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={C.hunter} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, margin: 0, letterSpacing: "-0.3px" }}>Password updated</h1>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                You can now sign in with your new password.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, margin: 0, letterSpacing: "-0.3px" }}>
                Set new password
              </h1>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 6, marginBottom: 20, lineHeight: 1.5 }}>
                Choose a new password for your Kapio account.
              </p>

              <Field label="New password">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  required
                  minLength={8}
                  style={inputStyle}
                />
              </Field>

              <Field label="Confirm new password">
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  style={inputStyle}
                />
              </Field>

              {confirm && password !== confirm && (
                <div style={{ fontSize: 12, color: C.red, fontWeight: 600, marginTop: -6, marginBottom: 10 }}>
                  Passwords do not match
                </div>
              )}

              {error && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    background: C.redBg,
                    border: `1px solid ${C.red}`,
                    borderRadius: 10,
                    fontSize: 12.5,
                    color: C.red,
                    fontWeight: 600,
                  }}
                >
                  {error}
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <Btn type="submit" full size="lg" disabled={!canSubmit}>
                  {busy ? "Updating…" : "Update password"}
                </Btn>
              </div>

              <p style={{ fontSize: 11, color: C.muted, marginTop: 12, lineHeight: 1.5, textAlign: "center" }}>
                Use at least 8 characters. Longer is better.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ fontSize: 11.5, fontWeight: 800, color: C.slate, marginBottom: 6, letterSpacing: 0.2 }}>{label}</div>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  padding: "11px 13px",
  fontSize: 13.5,
  fontFamily: "inherit",
  background: C.cream,
  border: "1px solid rgba(196,193,183,0.4)",
  borderRadius: 12,
  color: C.charcoal,
  outline: "none",
  boxSizing: "border-box",
};
